import * as Popover from "@radix-ui/react-popover";
import { CallIcon } from "assets/icons/Call";
import { SmsIcon } from "assets/icons/Sms";
import { useQuery } from "hooks/useQuery";
import { useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useGetAccountByIdQuery } from "store/queries/account";
import { useGetDirectPayLinkPublicConfigQuery } from "store/queries/directPayLinks";
import { useGetOrderOverviewQuery } from "store/queries/order";
import styled from "styled-components";

import { AccountSwitcher } from "../AccountSwitcher";
import { Logo } from "../Logo";
import { UserDropdown } from "../UserDropdown";

const HeaderStyled = styled.header`
  z-index: 999;
  height: var(--navbar-height);

  box-sizing: border-box;

  background-color: var(--color-white);
  border-bottom: 1px solid var(--color-black-1);

  padding-left: 24px !important;
  padding-right: 24px !important;
`;

const LinkButtonStyled = styled.div`
  color: #717885;
`;

const StyledPopoverContent = styled(Popover.Content)`
  z-index: 3500;

  color: var(--color-purple);

  border: 1px solid var(--color-black-1);
  background-color: var(--color-white);
`;

const StyledPopoverTrigger = styled(Popover.Trigger)`
  all: unset;
`;

const Placeholder = styled.div`
  width: 1px;
`;

const LeftSection = styled.div`
  gap: 16px;
  min-width: 0;

  display: flex;
  align-items: center;
`;

const RightSection = styled.div`
  gap: 16px;

  display: flex;
  align-items: center;
`;

type NavbarProps = {
  variant?: "unlogged";
};

export function Navbar({ variant }: NavbarProps): JSX.Element {
  const isUnloggedVersion = variant === "unlogged";

  const { id, linkId } = useParams();
  const query = useQuery();
  const location = useLocation();
  const accountId = query.get("accountId") || undefined;

  // Check if this is a DirectPay public link route
  const isDirectPayPublicLink = location.pathname.startsWith("/dp/");
  const isHospitalView = location.pathname.startsWith("/hospitals/view/");

  // On /hospitals/view/:id the route param IS the account id, so fall back to it
  // when the page doesn't have a ?accountId= query string.
  const effectiveAccountId = accountId || (isHospitalView ? id : undefined);

  const { data: overviewData } = useGetOrderOverviewQuery(id || "", {
    skip: id?.length !== 5,
  });

  const { data: accountByIdData } = useGetAccountByIdQuery(
    effectiveAccountId || "",
    {
      skip: !effectiveAccountId,
    }
  );

  // Fetch DirectPay link config for public links
  const { data: linkConfigData } = useGetDirectPayLinkPublicConfigQuery(
    linkId || "",
    {
      skip: !isDirectPayPublicLink || !linkId,
    }
  );

  const logoUrl = useMemo(() => {
    // For DirectPay public links, use the logo from linkConfig
    if (isDirectPayPublicLink && linkConfigData?.data?.accountLogoUrl) {
      return linkConfigData.data.accountLogoUrl;
    }

    // For other unlogged pages, use existing logic
    if (!accountByIdData && !overviewData) return "";

    return (
      accountByIdData?.data.accountLogoUrl || overviewData?.data.accountLogo
    );
  }, [overviewData, accountByIdData, linkConfigData, isDirectPayPublicLink]);

  // Any role on /hospitals/view/ sees the account's logo — there are only
  // SuperAdmin and HospitalStaff, and both should get it.
  const showLogo = Boolean(logoUrl) && (isUnloggedVersion || isHospitalView);

  return (
    <HeaderStyled className="w-full	p-3 fixed flex justify-content-between align-items-center">
      <LeftSection>
        {showLogo ? (
          <Logo
            accountLogo={logoUrl}
            variant={variant}
            accountId={
              isDirectPayPublicLink
                ? linkConfigData?.data?.accountId || null
                : overviewData?.data.accountId || effectiveAccountId || null
            }
          />
        ) : (
          <Placeholder />
        )}

        {!isUnloggedVersion ? <AccountSwitcher /> : null}
      </LeftSection>
      {!isUnloggedVersion ? (
        <RightSection>
          <UserDropdown />
        </RightSection>
      ) : (
        <Popover.Root>
          <StyledPopoverTrigger>
            <LinkButtonStyled className="cursor-pointer text-xs sm:text-base">
              Help
            </LinkButtonStyled>
          </StyledPopoverTrigger>

          <Popover.Portal>
            <StyledPopoverContent className="cursor-default gap-2 flex flex-column py-4	px-3 border-round-lg mt-3 mr-4">
              <div className="flex align-items-center gap-2">
                <CallIcon className="mt-1" />
                <p>(888) 918-2522</p>
              </div>

              <div className="flex align-items-center gap-2">
                <SmsIcon className="mt-1" />
                <p>support@clearhealthinc.com</p>
              </div>
            </StyledPopoverContent>
          </Popover.Portal>
        </Popover.Root>
      )}
    </HeaderStyled>
  );
}
